import { useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, FileText, Sparkles, FileImage } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { generatePhotoReport } from "@/lib/photoReportPdf";

export default function ProjectShareBar({ job }) {
  const [token, setToken] = useState(job?.share_token || "");
  const [log, setLog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [perPage, setPerPage] = useState(4);
  const [signoff, setSignoff] = useState(false);
  const shareUrl = token ? `${window.location.origin}/t/${token}` : "";

  const share = async () => {
    try { const { data } = await api.post(`/jobs/${job.id}/share`); setToken(data.token); toast.success("Public timeline link ready"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const copy = () => { navigator.clipboard?.writeText(shareUrl); toast.success("Link copied"); };
  const dailyLog = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/jobs/${job.id}/daily-log`); setLog(data); toast.success("Daily log generated"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const photoReport = async () => {
    setReportBusy(true);
    try {
      const { data } = await api.get(`/jobs/${job.id}/photo-report-data`);
      if (!data.count) { toast.error("No photos on this project yet"); setReportBusy(false); return; }
      await generatePhotoReport(data, { perPage, signoff });
      toast.success(`Branded report built · ${data.count} photos`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setReportBusy(false);
  };

  return (
    <div className="card-tac mt-6 p-5" data-testid="project-share-bar">
      <div className="mb-3 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><Share2 className="h-4 w-4 text-cyan-400" /> Share &amp; document</div>
      <div className="flex flex-wrap items-center gap-2">
        {!token
          ? <button data-testid="share-timeline-btn" onClick={share} className="btn-amber inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs"><Share2 className="h-3.5 w-3.5" /> Create public timeline link</button>
          : (
            <div className="flex flex-1 items-center gap-2">
              <input data-testid="share-url" readOnly value={shareUrl} className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-[#0b0f17] px-3 py-2 text-xs text-cyan-300" />
              <button data-testid="share-copy-btn" onClick={copy} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:border-cyan-500"><Copy className="h-3.5 w-3.5" /> Copy</button>
              <a href={shareUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:border-cyan-500">Open</a>
            </div>
          )}
        <button data-testid="daily-log-btn" onClick={dailyLog} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs text-slate-200 hover:border-amber-500 disabled:opacity-50">
          {busy ? <Sparkles className="h-3.5 w-3.5 animate-pulse text-amber-400" /> : <FileText className="h-3.5 w-3.5 text-amber-400" />} {busy ? "Generating…" : "AI daily log"}
        </button>
        <button data-testid="photo-report-btn" onClick={photoReport} disabled={reportBusy} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs text-slate-200 hover:border-cyan-500 disabled:opacity-50">
          {reportBusy ? <Sparkles className="h-3.5 w-3.5 animate-pulse text-cyan-400" /> : <FileImage className="h-3.5 w-3.5 text-cyan-400" />} {reportBusy ? "Building…" : "Branded photo report"}
        </button>
        <div className="flex items-center gap-2 rounded-full border border-slate-800 bg-[#0b0f17] px-3 py-1.5 text-[11px] text-slate-400">
          <span>Layout</span>
          <select data-testid="report-layout-select" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} className="rounded border border-slate-700 bg-[#0d1420] px-1.5 py-0.5 text-slate-200 focus:outline-none">
            <option value={1}>1 / page</option>
            <option value={2}>2 / page</option>
            <option value={4}>4 / page</option>
          </select>
          <label className="inline-flex cursor-pointer items-center gap-1">
            <input data-testid="report-signoff-toggle" type="checkbox" checked={signoff} onChange={(e) => setSignoff(e.target.checked)} className="accent-amber-500" /> Sign-off page
          </label>
        </div>
      </div>
      {token && <p className="mt-2 font-mono-tac text-[10px] text-slate-600">Anyone with this link sees a live, auto-updating photo timeline - no login needed.</p>}
      {log && (
        <div className="mt-3 rounded-xl border border-slate-800 bg-[#0d1420] p-3" data-testid="daily-log-result">
          <div className="mb-1 font-mono-tac text-[10px] uppercase text-slate-500">Daily log · {log.date} · {log.photo_count} photos</div>
          <pre className="whitespace-pre-wrap font-sans text-xs text-slate-200">{log.summary}</pre>
        </div>
      )}
    </div>
  );
}
